import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../../store/authStore';
import { createShort, deleteShort, getShortById, getShorts, updateShort } from '../../api/admin/adminShorts';
import { useNavigate } from 'react-router-dom';


export const useShorts = (params) => {
  const { setError } = useAuthStore();
  
  const {
    data: shortsData,
    isLoading,
    error,
    refetch
  } = useQuery(['shorts', params], () => getShorts(params), {
    onError: (error) => {
      setError(error?.response?.data?.message || 'Failed to fetch shorts');
    },
    retry: false,
    staleTime: 300000, // Consider data fresh for 5 minutes
    cacheTime: 3600000, // Keep data in cache for 1 hour
  });

  return {
    shortsData,
    isLoading,
    error,
    refetchShorts: refetch
  };
};

export const useCreateShort = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();
  const navigate=useNavigate()

  const mutation = useMutation(createShort, {
    onSuccess: () => {
      toast.success('Short created successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      queryClient.invalidateQueries('shorts');
      navigate("/admin/dashboard", {
        state: { data: "media" },
      });
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to create short';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });

  return {
    createShort: mutation.mutate,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
    error: mutation.error,
  };
};

export const useUpdateShort = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  const mutation = useMutation(
    ({ shortId, formData }) => updateShort(shortId, formData),
    {
      onSuccess: () => {
        toast.success('Short updated successfully!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        queryClient.invalidateQueries('shorts');
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to update short';
        setError(errorMessage);
        toast.error(errorMessage);
      },
    }
  );

  return {
    updateShort: mutation.mutate,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
    error: mutation.error,
  };
};

export const useDeleteShort = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  const mutation = useMutation(deleteShort, {
    onSuccess: () => {
      toast.success('Short deleted successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      queryClient.invalidateQueries('shorts');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to delete short';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });

  return {
    deleteShort: mutation.mutate,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
    error: mutation.error,
  };
};

export const useShortById = (shortId) => {
  const { setError } = useAuthStore();
  
  const {
    data: shortData,
    isLoading,
    error,
    refetch
  } = useQuery(['short', shortId], () => getShortById(shortId), {
    onError: (error) => {
      setError(error?.response?.data?.message || `Failed to fetch short with ID ${shortId}`);
    },
    retry: false,
    enabled: !!shortId, // Only run query if shortId exists
    staleTime: 300000, // Consider data fresh for 5 minutes
    cacheTime: 3600000, // Keep data in cache for 1 hour
  });

  return {
    shortData,
    isLoading,
    error,
    refetchShort: refetch
  };
};